import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  IconButton,
  MenuItem,
  TextField,
  Switch,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import EditIcon from '@mui/icons-material/Edit';
import { ConfirmationDialogMAIQ } from 'app/components/ReusableUIComponents/LowLevelComponents/ConfirmationDialogMAIQ';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

import { PLAN_COLORS } from '../constants';
import type { PlanConfig, Segment } from '../types';
import type { useNurturingPlan } from '../hooks/useNurturingPlan';
import { LeadSourceTimeline } from './LeadSourceTimeline';
import { NumberField } from './NumberField';
import { Pill } from './Pill';
import { SortableSegmentCard } from './SortableSegmentCard';
import { WarmupScheduleEditor } from './WarmupScheduleEditor';
import {
  countUnusedSegments,
  segmentNameOptionsForCard,
} from '../utils/dealerSegmentOptions';
import { formatCalendarDateForApi, parseCalendarDateFromApi } from 'utils/dateUtils';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import type { DateTime } from 'luxon';

type LookBackDateRangeValue = [DateTime | null, DateTime | null];

type PlanController = ReturnType<typeof useNurturingPlan>;

interface ConfigPanelProps {
  config: PlanConfig;
  rawConfig: PlanController['rawConfig'];
  segments: Segment[];
  pctSum: number;
  onConfigChange: PlanController['onConfigChange'];
  onWarmupScheduleChange: PlanController['onWarmupScheduleChange'];
  onEagerChange: PlanController['onEagerChange'];
  onLookBackRangeChange: (range: {
    startDate: string | null;
    endDate: string | null;
  }) => void;
  onSegmentNameChange: PlanController['onSegmentNameChange'];
  onSegmentStartDayChange: PlanController['onSegmentStartDayChange'];
  onPctChange: PlanController['onPctChange'];
  onAddSegment: PlanController['onAddSegment'];
  onDeleteSegment: PlanController['onDeleteSegment'];
  onReorderSegments: PlanController['onReorderSegments'];
  onGeneratePlan: PlanController['handleGeneratePlan'];
  generating: boolean;
  canGenerate: boolean;
  availableSegments: string[];
  segmentsConfigError: string | null;
  canAddSegment: boolean;
  hasLaunchDate: boolean;
  launchDateDisplay: string | null;
  leadAnalysisLoading: boolean;
}

const SectionTitle = ({
  children,
  isNew,
  action,
  suffix,
}: {
  children: ReactNode;
  isNew?: boolean;
  action?: ReactNode;
  suffix?: ReactNode;
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 0.75,
      mb: 1.25,
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, flex: 1 }}>
      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 700,
          color: 'text.secondary',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          lineHeight: 1.4,
        }}
      >
        {children}
      </Typography>
      {suffix}
      {isNew && (
        <Box
          sx={{
            fontSize: 9,
            bgcolor: PLAN_COLORS.orangeBg,
            color: PLAN_COLORS.orange,
            px: 0.625,
            borderRadius: 0.5,
            fontWeight: 700,
            letterSpacing: '0.05em',
          }}
        >
          NEW
        </Box>
      )}
    </Box>
    {action ? <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>{action}</Box> : null}
  </Box>
);

const Field = ({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: ReactNode;
  children: ReactNode;
}) => (
  <Box sx={{ mb: 1.25 }}>
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 0.625,
      }}
    >
      <Typography sx={{ fontSize: 12, fontWeight: 500, color: 'text.secondary' }}>
        {label}
      </Typography>
      {hint && (
        <Typography sx={{ fontSize: 10, color: 'text.disabled', fontFamily: 'monospace' }}>
          {hint}
        </Typography>
      )}
    </Box>
    {children}
  </Box>
);

const Section = ({ children }: { children: ReactNode }) => (
  <Box sx={{ mb: 2.75 }}>{children}</Box>
);

const UNSOLD_LEAD_PICKUP_OPTIONS = [7, 10, 15, 21, 30];
const NEW_LEAD_ENTRY_OPTIONS = [30, 60, 90];
const RETARGETING_OPTIONS = [1, 2, 3, 4, 5];

const compactDateRangeFieldSx = {
  '& .MuiOutlinedInput-root': {
    minHeight: 'unset',
  },
  '& .MuiOutlinedInput-input': {
    py: 0.75,
    px: 1,
    fontSize: 13,
  },
  '& .MuiInputLabel-root': {
    display: 'none',
  },
} as const;

export const ConfigPanel = ({
  config,
  rawConfig,
  segments,
  pctSum,
  onConfigChange,
  onWarmupScheduleChange,
  onEagerChange,
  onLookBackRangeChange,
  onSegmentNameChange,
  onSegmentStartDayChange,
  onPctChange,
  onAddSegment,
  onDeleteSegment,
  onReorderSegments,
  onGeneratePlan,
  generating,
  canGenerate,
  availableSegments,
  segmentsConfigError,
  canAddSegment,
  hasLaunchDate,
  launchDateDisplay,
  leadAnalysisLoading,
}: ConfigPanelProps) => {
  const [deleteConfirmIndex, setDeleteConfirmIndex] = useState<number | null>(null);
  const [warmupEditing, setWarmupEditing] = useState(false);
  const sumOk = pctSum === 100;
  const deleteTarget =
    deleteConfirmIndex != null ? segments[deleteConfirmIndex] : undefined;

  const unusedSegmentCount = useMemo(
    () => countUnusedSegments(availableSegments, segments),
    [availableSegments, segments],
  );
  const newStartHint =
    rawConfig.newStart <= rawConfig.ramp
      ? `during ramp (day ${rawConfig.newStart})`
      : `after ramp (day ${rawConfig.newStart})`;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    onReorderSegments(String(active.id), String(over.id));
  };

  const lookBackRangeValue: LookBackDateRangeValue = [
    rawConfig.look_back_start_date
      ? parseCalendarDateFromApi(rawConfig.look_back_start_date)
      : null,
    rawConfig.look_back_end_date
      ? parseCalendarDateFromApi(rawConfig.look_back_end_date)
      : null,
  ];

  const handleLookBackRangeChange = (newValue: LookBackDateRangeValue) => {
    const [start, end] = newValue;
    onLookBackRangeChange({
      startDate:
        start?.isValid === true
          ? formatCalendarDateForApi(start.startOf('day'))
          : null,
      endDate:
        end?.isValid === true ? formatCalendarDateForApi(end.startOf('day')) : null,
    });
  };

  return (
    <Box
      sx={{
        width: 300,
        minWidth: 300,
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        flexShrink: 0,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 2.5, pb: 2 }}>
        {segmentsConfigError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {segmentsConfigError}
          </Alert>
        ) : null}
        <Section>
          <SectionTitle>CRM Pool</SectionTitle>
          <Field label="Total nurturable leads" hint="valid email + opted-in">
            <NumberField
              value={rawConfig.total}
              onChange={(v) => onConfigChange('total', v)}
              min={100}
              disabled={leadAnalysisLoading}
              aria-label="Total nurturable leads"
            />
          </Field>
          <Field label="New unsold leads / month" hint="30+ day leads">
            <NumberField
              value={rawConfig.monthly}
              onChange={(v) => onConfigChange('monthly', v)}
              min={1}
              disabled={leadAnalysisLoading}
              aria-label="New unsold leads per month"
            />
          </Field>
          <Field label="Lookback window" hint="months">
            <NumberField
              value={rawConfig.look}
              onChange={(v) => onConfigChange('look', v)}
              min={12}
              max={60}
              disabled
              aria-label="Lookback window"
            />
          </Field>
        </Section>

        <Section>
          <SectionTitle
            suffix={
              hasLaunchDate && launchDateDisplay ? (
                <Pill color={PLAN_COLORS.green} bg={PLAN_COLORS.greenBg}>
                  Launched: {launchDateDisplay}
                </Pill>
              ) : undefined
            }
          >
            Launch Settings
          </SectionTitle>

          <Field label="Unsold Lead Pickup Threshold" hint="days">
            <TextField
              select
              size="small"
              fullWidth
              value={rawConfig.unsold_lead_pickup_threshold_days}
              onChange={(e) =>
                onConfigChange(
                  'unsold_lead_pickup_threshold_days',
                  Number(e.target.value),
                )
              }
              sx={{
                '& .MuiOutlinedInput-input': { py: 0.75, fontSize: 13 },
                '& .MuiSelect-select': { py: 0.75, fontSize: 13 },
              }}
            >
              {UNSOLD_LEAD_PICKUP_OPTIONS.map((days) => (
                <MenuItem key={days} value={days}>
                  {days} days
                </MenuItem>
              ))}
            </TextField>
          </Field>

          <Field label="New Lead Entry Threshold" hint="days">
            <TextField
              select
              size="small"
              fullWidth
              value={rawConfig.new_lead_entry_threshold_days}
              onChange={(e) =>
                onConfigChange(
                  'new_lead_entry_threshold_days',
                  Number(e.target.value),
                )
              }
              sx={{
                '& .MuiOutlinedInput-input': { py: 0.75, fontSize: 13 },
                '& .MuiSelect-select': { py: 0.75, fontSize: 13 },
              }}
            >
              {NEW_LEAD_ENTRY_OPTIONS.map((days) => (
                <MenuItem key={days} value={days}>
                  {days} days
                </MenuItem>
              ))}
            </TextField>
          </Field>

          <Field label="Retargeting Eligibility Cutoff" hint="years">
            <TextField
              select
              size="small"
              fullWidth
              value={rawConfig.retargeting_eligibility_cutoff_years}
              onChange={(e) =>
                onConfigChange(
                  'retargeting_eligibility_cutoff_years',
                  Number(e.target.value),
                )
              }
              sx={{
                '& .MuiOutlinedInput-input': { py: 0.75, fontSize: 13 },
                '& .MuiSelect-select': { py: 0.75, fontSize: 13 },
              }}
            >
              {RETARGETING_OPTIONS.map((years) => (
                <MenuItem key={years} value={years}>
                  {years} {years === 1 ? 'year' : 'years'}
                </MenuItem>
              ))}
            </TextField>
          </Field>

          <Field
            label="Look Back Date"
            hint={
              leadAnalysisLoading ? (
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                  <CircularProgress size={12} />
                  Loading lead analysis...
                </Box>
              ) : (
                'Select start and end date'
              )
            }
          >
            <LocalizationProvider dateAdapter={AdapterLuxon}>
              <DateRangePicker
                value={lookBackRangeValue}
                disabled={leadAnalysisLoading}
                onChange={handleLookBackRangeChange}
                sx={{
                  width: '100%',
                  '& .MuiDateRangePickerInput-separator': {
                    fontSize: 13,
                    mx: 0.5,
                  },
                }}
                slotProps={{
                  textField: ({ position }) => ({
                    size: 'small',
                    label: '',
                    placeholder: position === 'start' ? 'Start' : 'End',
                    sx: compactDateRangeFieldSx,
                    InputProps: leadAnalysisLoading
                      ? {
                        endAdornment: <CircularProgress size={14} sx={{ mr: 0.5 }} />,
                      }
                      : undefined,
                  }),
                }}
              />
            </LocalizationProvider>
          </Field>
        </Section>

        <Section>
          <SectionTitle>Email Sending</SectionTitle>
          <Field label="Initial send frequency" hint="days between emails">
            <NumberField
              value={rawConfig.freqI}
              onChange={(v) => onConfigChange('freqI', v)}
              min={1}
              disabled
              aria-label="Initial send frequency"
            />
          </Field>
          <Field label="Steady-state frequency" hint="days between emails">
            <NumberField
              value={rawConfig.freqS}
              onChange={(v) => onConfigChange('freqS', v)}
              min={14}
              disabled
              aria-label="Steady-state frequency"
            />
          </Field>
        </Section>

        <Section>
          <SectionTitle
            isNew
            action={
              <IconButton
                size="small"
                onClick={() => setWarmupEditing((v) => !v)}
                aria-label={
                  warmupEditing ? 'Done editing warm-up schedule' : 'Edit warm-up schedule'
                }
                sx={{
                  p: 0.25,
                  color: warmupEditing ? PLAN_COLORS.orange : 'text.secondary',
                  '&:hover': { color: PLAN_COLORS.orange, bgcolor: PLAN_COLORS.orangeBg },
                }}
              >
                <EditIcon sx={{ fontSize: 16 }} />
              </IconButton>
            }
          >
            Address Warm-up Schedule
          </SectionTitle>
          <WarmupScheduleEditor
            steps={rawConfig.warmupSchedule}
            onChange={onWarmupScheduleChange}
            disabled={!warmupEditing}
          />
        </Section>

        <Section>
          <SectionTitle isNew>Batch Cadence</SectionTitle>
          <Field label="Ramp-up batch cadence" hint={`fixed at 1 day (days 1–${config.ramp})`}>
            <NumberField
              value={1}
              onChange={() => { }}
              min={1}
              max={1}
              disabled
              aria-label="Ramp-up batch cadence"
            />
          </Field>
          <Field label="Steady-state batch cadence" hint={`fixed at 1 day (after day ${config.ramp})`}>
            <NumberField
              value={1}
              onChange={() => { }}
              min={1}
              max={1}
              disabled
              aria-label="Steady-state batch cadence"
            />
          </Field>
          <FormControlLabel
            sx={{ mt: 0.5, mb: 0.5, alignItems: 'flex-start' }}
            control={
              <Switch
                size="small"
                checked={rawConfig.eagerProvisioning}
                onChange={(e) => onEagerChange(e.target.checked)}
                inputProps={{ 'aria-label': 'Eager address provisioning' }}
              />
            }
            label={
              <Box>
                <Typography sx={{ fontSize: 12, fontWeight: 500 }}>Eager address provisioning</Typography>
                <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>
                  {rawConfig.eagerProvisioning
                    ? 'All addresses start Day 1 (eager)'
                    : 'Reactive — provision when demand nears capacity'}
                </Typography>
              </Box>
            }
          />
        </Section>

        <Section>
          <SectionTitle isNew>Lead Windows</SectionTitle>
          <Field label="Start adding new leads from day" hint={newStartHint}>
            <NumberField
              value={rawConfig.newStart}
              onChange={(v) => onConfigChange('newStart', v)}
              min={1}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: PLAN_COLORS.bothBg,
                  '& fieldset': { borderColor: PLAN_COLORS.both },
                },
              }}
              aria-label="Start adding new leads from day"
            />
          </Field>
          <Box
            sx={{
              fontSize: 11,
              color: PLAN_COLORS.both,
              lineHeight: 1.4,
              p: 1,
              bgcolor: PLAN_COLORS.bothBg,
              borderRadius: 0.625,
              borderLeft: `2px solid ${PLAN_COLORS.both}`,
              mb: 1.25,
            }}
          >
            Past leads (lookback window) load from <strong>Day 1</strong>. New unsold leads
            (30+ days) begin loading from the day set here. Set to <strong>1</strong> to load
            both simultaneously from launch.
          </Box>
          <LeadSourceTimeline config={config} />
        </Section>

        <Section>
          <SectionTitle
            action={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                {!segmentsConfigError ? (
                  <Box
                    component="span"
                    title={
                      unusedSegmentCount === 1
                        ? '1 segment available to add'
                        : `${unusedSegmentCount} segments available to add`
                    }
                    sx={{
                      minWidth: 20,
                      height: 20,
                      px: 0.5,
                      borderRadius: 10,
                      bgcolor: unusedSegmentCount > 0 ? PLAN_COLORS.orange : 'grey.400',
                      color: '#fff',
                      fontSize: 11,
                      fontWeight: 700,
                      lineHeight: '20px',
                      textAlign: 'center',
                      fontFamily: 'monospace',
                      flexShrink: 0,
                    }}
                  >
                    {unusedSegmentCount}
                  </Box>
                ) : null}
                <Button
                  size="small"
                  variant="outlined"
                  onClick={onAddSegment}
                  disabled={!canAddSegment}
                  title={
                    canAddSegment
                      ? 'Add a dealer segment'
                      : 'All dealer segments are already in use or segment settings are missing'
                  }
                  sx={{
                    py: 0.25,
                    px: 1.25,
                    fontSize: 11,
                    minWidth: 0,
                    color: PLAN_COLORS.orange,
                    borderColor: PLAN_COLORS.orange,
                    '&:hover': { borderColor: PLAN_COLORS.orange, bgcolor: PLAN_COLORS.orangeBg },
                  }}
                >
                  + Add
                </Button>
              </Box>
            }
          >
            Segments
          </SectionTitle>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={segments.map((s) => String(s.id))}
              strategy={verticalListSortingStrategy}
            >
              {segments.map((segment, i) => (
                <SortableSegmentCard
                  key={segment.id}
                  segment={segment}
                  index={i}
                  config={config}
                  canDelete={false}
                  showDeleteIcon={segments.length > 1}
                  onNameChange={onSegmentNameChange}
                  onPctChange={onPctChange}
                  onStartDayChange={onSegmentStartDayChange}
                  onRequestDelete={setDeleteConfirmIndex}
                  nameOptions={segmentNameOptionsForCard(
                    availableSegments,
                    segments,
                    i,
                  )}
                />
              ))}
            </SortableContext>
          </DndContext>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.75 }}>
            <Typography
              sx={{
                fontSize: 11,
                fontFamily: 'monospace',
                fontWeight: 600,
                color: sumOk ? PLAN_COLORS.green : PLAN_COLORS.newBatch,
              }}
            >
              Σ = {pctSum}%{sumOk ? ' ✓' : ' (rebalances on next edit)'}
            </Typography>
          </Box>
        </Section>
      </Box>

      <Box
        sx={{
          flexShrink: 0,
          px: 2.5,
          py: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Button
          fullWidth
          variant="contained"
          color="primary"
          startIcon={
            generating ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <AutoAwesomeIcon />
            )
          }
          onClick={onGeneratePlan}
          disabled={generating || !canGenerate}
          sx={{
            py: 1.25,
            fontWeight: 600,
            textTransform: 'none',
            bgcolor: PLAN_COLORS.orange,
            '&:hover': { bgcolor: PLAN_COLORS.orange, filter: 'brightness(0.92)' },
            '&.Mui-disabled': { bgcolor: 'action.disabledBackground' },
          }}
        >
          {generating ? 'Generating...' : 'Generate Plan'}
        </Button>
      </Box>

      <ConfirmationDialogMAIQ
        open={deleteConfirmIndex != null}
        onClose={() => setDeleteConfirmIndex(null)}
        onConfirm={() => {
          if (deleteConfirmIndex != null) {
            onDeleteSegment(deleteConfirmIndex);
          }
          setDeleteConfirmIndex(null);
        }}
        title="Remove segment?"
        message={
          deleteTarget
            ? `Remove "${deleteTarget.name}" from this nurturing plan? You can add it again from the segment dropdown. This action cannot be undone.`
            : 'Remove this segment from the nurturing plan? This action cannot be undone.'
        }
        confirmLabel="Remove"
        cancelLabel="Cancel"
        confirmRequiredText="delete"
      />
    </Box>
  );
};
